import { useTranslate } from '@tolgee/react';
import { message, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { syncVoucher } from 'services/api/conversation';
import { useMemoizedFn } from 'ahooks';
import dayjs from 'lib/dayjs';
import { isTiKTok } from 'lib/utils/isTikTok';
import { formatLoggerData, logger } from 'services/logger';
import { useState, useMemo } from 'react';

interface VoucherSyncProps {
  storeId: string;
  channel: General.PlatformUpperCaseTunnel;
  lastSyncTime?: string;
  onSuccess: () => void;
}

export function VoucherSync(props: VoucherSyncProps) {
  const { storeId, channel, lastSyncTime, onSuccess } = props;

  const [syncing, setSyncing] = useState(false);

  const { t } = useTranslate();

  const isTiktok = isTiKTok(channel);

  const refreshTooltipTitle = useMemo(() => {
    if (lastSyncTime && lastSyncTime !== '0') {
      const formatUpdateTime = dayjs(Number(lastSyncTime) * 1000).format('YYYY.MM.DD HH:mm:ss');
      return (
        <>
          {!isTiktok && <div>{t('scene.chatWorkspace.couponTab.tooltip.sync')}</div>}
          <div>
            {t('scene.chatWorkspace.couponTab.tooltip.syncLastTime', { time: formatUpdateTime })}
          </div>
        </>
      );
    }
    return null;
  }, [lastSyncTime, t, isTiktok]);

  const handleRefresh = useMemoizedFn(() => {
    setSyncing(true);

    syncVoucher({
      internalStoreId: parseInt(storeId, 10),
    })
      .then(() => {
        onSuccess?.();
        setSyncing(false);

        message.success(t('scene.chatWorkspace.couponTab.feedback.syncSuccess'));
      })
      .catch((err) => {
        logger.info(
          `syncVoucherError, ${formatLoggerData({
            message: err?.message || '',
            status: err?.status || '',
          })}`,
        );
        message.error(t('scene.chatWorkspace.couponTab.feedback.syncFailed'));
      })
      .finally(() => {
        setSyncing(false);
      });
  });

  return (
    <Tooltip
      title={refreshTooltipTitle}
      visible={lastSyncTime !== '0' || syncing ? undefined : false}
      placement="left"
    >
      <SyncOutlined
        className="cursor-pointer"
        onClick={handleRefresh}
        spin={syncing}
        disabled={syncing}
      />
    </Tooltip>
  );
}
